import { Button, Dropdown, Paginator } from '@epo/epods-react-components';
import axios from 'axios';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../../../types';
import { downloadFile } from '../../../../../utils/download';
import { BACKEND_URL } from '../../../../../variables';
import ErrorMessage from '../../../Search/components/ErrorMessage';
import { SearchResultsObj } from '../../../Search/components/SearchResults/types';

import { numberWithSpaces } from '../../../Search/components/SearchResults/utils';

interface Props {
    children: ReactElement;
    itemCount: number;
    item: SearchResultsObj;
    itemPosition: number | null;
    onPageChange: any;
    handleDocLayout: any;
    documentType: string | undefined;
    kindCode?: string;
    publicationNumber?: string;
    publicationDate?: string;
}

const DocumentLayout = (props: Props) => {
    const [t] = useTranslation();
    const { children, itemCount, item, documentType, itemPosition, onPageChange,handleDocLayout } = props;
    const navigate = useNavigate();
    const [error, setError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<any>(t('DOCUMENT_VIEW::ERROR_MSG'));
    const data = useSelector((state: RootState) => state.otherKindCodes.data);   
    const onDownloadFileClick = (path: string | null, type: 'pdf' | 'zip') => {
        const url = `${BACKEND_URL}/publications/${type}?path=${path}`;

        axios
            .get(url, {
                responseType: 'blob',
            })
            .then((response) => {
                downloadFile(response);
            })
            .catch((err) => {
                setError(true);
                setErrorMessage(err.message);
            });
    };

    const handleCloseError = () => {
        setError(false);
        setErrorMessage(t('DOCUMENT_VIEW::ERROR_MSG'));
    };

    const publicationNumberOptions = () =>
        data?.content.map((i) => ({
            id: `${i.publicationNumber} ${i.publicationKind} ${i.publicationDate}`,
            label: `EP ${i.publicationNumber} ${i.publicationKind} `,
        }));

    const onPublicationDrodownChange = (val: any) => {
        const [number, kindCode, publicationDate] = val.option.id.split(' ');
        handleDocLayout(`/document/${documentType}/${number}/${kindCode}/${publicationDate}`);
        return navigate(`/document/${documentType}/${number}/${kindCode}/${publicationDate}`);
    };

    const getDocumentTypeOptions = () => {
        if (item.wipoLink) {
            return [
                {
                    id: 'xml',
                    label: 'XML',
                },
            ];
        }
        return [
            {
                id: 'pdf',
                label: 'PDF',
            },
            {
                id: 'xml',
                label: 'XML',
            },
        ];
    };

    useEffect(() => {
        let paginationText = document.getElementsByClassName('p-paginator-current');
        if (paginationText && paginationText.length > 0) {
            let text = paginationText[0].textContent?.split(' ');
            if (text && text.length === 3) {
                let first = text[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                let of = text[1];
                let total = text[2].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

                document.getElementsByClassName('p-paginator-current')[0].textContent = first + ' ' + of + ' ' + total;
            }
        }
    },[]);

    const totalElements = numberWithSpaces(itemCount);

    return (
        <>
            {documentType !== 'pdf' ? (
                <div>
                    <p>
                        <span>{t('DOCUMENT_VIEW::NOTICE')}</span>
                        <span id="translationDisclaimer"></span>
                    </p>
                </div>
            ) : null}
            <div
                className="border border-ds-light d-flex justify-content-between"
                style={{
                    backgroundColor: 'var(--epods--color-bg-canvas)',
                    padding: '9px 36px 9px 21px',
                }}
            >
                <div>
                    <strong>{itemCount > 1 && t('DOCUMENT_VIEW::DOCUMENTS_FOUND', { totalElements })}</strong>
                </div>
                <Dropdown
                    onChange={(val: any) =>
                        navigate(
                            `/document/${val.option.id}/${item.publicationNumber}/${item.publicationKind}/${item.publicationDate}`,
                        )
                    }
                    options={getDocumentTypeOptions()}
                    preset="select"
                    size="default"
                    value={[
                        {
                            id: documentType,
                            label: documentType,
                        },
                    ]}
                />
            </div>
            <div
                className="border border-ds-light"
                style={{
                    backgroundColor: 'var(--epods--color-bg-canvas)',
                    padding: '9px 36px 9px 21px',
                    marginTop: '-1px',
                }}
            >
                <Button
                    style={{ marginRight: '18px' }}
                    onClick={() => onDownloadFileClick(item.zipFile, 'zip')}
                    theme="tertiary"
                    data-testid="downloadZip"
                >
                    {t('DOCUMENT_VIEW::DOWNLOAD_ZIP')}
                </Button>
                {!item.wipoLink && (
                    <Button
                        style={{ marginRight: '18px' }}
                        onClick={() => onDownloadFileClick(item.pdfFile, 'pdf')}
                        theme="tertiary"
                        data-testid="downloadPdf"
                    >
                        {t('DOCUMENT_VIEW::DOWNLOAD_PDF')}
                    </Button>
                )}
            </div>
            <div
                className="d-flex justify-content-between border border-ds-light"
                style={{
                    backgroundColor: 'var(--epods--color-bg-canvas)',
                    padding: '9px 36px 9px 21px',
                    marginTop: '-1px',
                }}
            >
                <div>
                    <div style={{ display: 'inline-block', paddingRight: '18px', fontSize: '14px' }}>
                        {t('DOCUMENT_VIEW::PUBLICATION_NUMBER')}
                    </div>
                    <div style={{ minWidth: '220px', display: 'inline-block' }}>
                        <Dropdown
                            data-testid="dropdown"
                            onChange={onPublicationDrodownChange}
                            options={publicationNumberOptions()}
                            preset="select"
                            size="default"
                            value={[
                                {
                                    id: `${item.publicationNumber} ${item.publicationKind}`,
                                    label: `EP ${item.publicationNumber} ${item.publicationKind}`,
                                },
                            ]}
                            disabled={publicationNumberOptions()?.length === 1}
                        />
                    </div>
                </div>


                {itemCount > 1 && itemPosition !== null && (
                    <div>
                        <Paginator
                            first={itemPosition}
                            onPageChange={onPageChange}
                            page={itemPosition}
                            paginatorTemplate="Label"
                            rows={1}
                            setCurrentPage={() => {
                                // this is intentional
                            }}
                            setCustomFirst={() => {
                                // this is intentional
                            }}
                            totalRecords={itemCount}
                        />
                    </div>
                )}
            </div>
            {error && (
                <div className="d-flex justify-content-center mt-l">
                    <ErrorMessage closeMsg={handleCloseError} errorMsg={errorMessage} />
                </div>
            )}
            <div>{children}</div>
        </>
    );
};

export default DocumentLayout;
